import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import map from 'lodash/map';

import answerSearchResource from 'src/actions/answers/answerSearchResource';
import publicAnswersResource from 'src/actions/answers/publicAnswersResource';
import answerTitlesResource from 'src/actions/answers/answerTitlesResource';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';

import { PublicAnswer } from 'src/models/Answers/AnswerModel';

export const useAnswerTitles = helpCenterId => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(answerTitlesResource.effects.fetch({ helpCenterId: helpCenterId }));
  }, [helpCenterId, dispatch]);

  const { loadingState, value } = useDocumentResource(answerTitlesResource, {
    fetch: false,
  });

  const answerTitles = Array.isArray(value) ? value : [];

  return [loadingState, answerTitles];
};

export const usePublicAnswers = language => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(publicAnswersResource.effects.fetch({ language: language }));
  }, [language, dispatch]);

  const { loadingState, value } = useDocumentResource(publicAnswersResource, {
    fetch: false,
  });

  const answers = Array.isArray(value) ? value : [];
  const publicAnswers = getAnswerOptions(answers);
  return [loadingState, publicAnswers];
};

const getAnswerOptions = (rawAnswers: PublicAnswer[] = []) => {
  return map(rawAnswers, answer => ({
    label: answer.name,
    value: answer.id,
  }));
};

export const usePublicAnswersSearch = (language, audienceId) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(answerSearchResource.effects.request({ language, audienceId, text: '' }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { loadingState, value: answerSearchResults = [] } = useDocumentResource(answerSearchResource, {
    fetch: false,
  });

  const rawPublicAnswers = getPublicAnswersForLanguageAndAudience(answerSearchResults, language, audienceId);

  const publicAnswers = getAnswerOptions(rawPublicAnswers);

  return [loadingState, publicAnswers];
};

const getPublicAnswersForLanguageAndAudience = (answers, language, audienceId) => {
  const publicAnswers: PublicAnswer[] = [];
  for (const answer of answers) {
    if (audienceId && !answer.audienceIds?.includes(audienceId)) {
      continue;
    }
    const selfServiceContent = answer.contents?.find(
      content => content?.selfService?.bodyHtml && content.language === language
    );
    if (!selfServiceContent) {
      continue;
    }

    const selfServiceAnswer = {
      id: answer.id,
      audienceIds: answer?.audienceIds ?? [],
      bodyHtml: selfServiceContent.selfService.bodyHtml,
      language: selfServiceContent.language,
      name: selfServiceContent.selfService.name,
    };
    publicAnswers.push(selfServiceAnswer);
  }
  return publicAnswers;
};
